import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { APP_MODAL_PORTAL_ID, Z_INDEX_MODAL_OVERLAY } from "constants/alert.constants";

export type ModalPortalVariant = "side" | "center";

export interface ModalPortalRootProps {
  open: boolean;
  children: React.ReactNode;
  variant?: ModalPortalVariant;
  className?: string;
}

/** Keep the overlay catching clicks briefly after close so the same X-click
 * cannot fall through to a View button underneath and reopen the modal. */
const CLOSE_CLICKTHROUGH_GUARD_MS = 350;

const sideShellStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: Z_INDEX_MODAL_OVERLAY,
};

const centerShellStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: Z_INDEX_MODAL_OVERLAY,
};

function resolvePortalContainer(): HTMLElement | null {
  if (typeof document === "undefined") {
    return null;
  }
  return document.getElementById(APP_MODAL_PORTAL_ID) ?? document.body;
}

export default function ModalPortalRoot({
  open,
  children,
  variant = "side",
  className,
}: ModalPortalRootProps) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [captureClicks, setCaptureClicks] = useState(open);

  useEffect(() => {
    setContainer(resolvePortalContainer());
  }, []);

  useEffect(() => {
    if (open) {
      setCaptureClicks(true);
      return undefined;
    }
    const timer = window.setTimeout(() => setCaptureClicks(false), CLOSE_CLICKTHROUGH_GUARD_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (!container) {
    return null;
  }

  const style = variant === "center" ? centerShellStyle : sideShellStyle;

  return createPortal(
    <div
      className={className ?? (variant === "side" ? "side-modal-root" : "center-modal-root")}
      style={{
        ...style,
        pointerEvents: captureClicks ? "auto" : "none",
      }}
      aria-hidden={!open}
    >
      {children}
    </div>,
    container
  );
}
